import type { EventActivity, User } from '@paintscout/api'
import { usePollingInterval } from 'src/hooks/usePollingInterval'
import { useSaveEventMutation, useSearchActivitiesQuery, useSearchUsersQuery } from '@paintscout/api'
import { filterEvents } from '@paintscout/util'
import { mapActivitiesToCalendarEvents } from '@paintscout/util/builder'
import { useProcessEvent, useLocalStorage } from 'shared/hooks'
import { useDialogs, useClientOptions, useUser } from '@paintscout/ui-business'
import type { Filter } from 'json-to-lucene'
import { jsonToLucene } from 'json-to-lucene'
import { useSnackbar } from 'notistack'
import type { CalendarEvent, EventType } from 'paintscout'
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import type { View } from 'react-big-calendar'
import { useLocation } from 'react-router-dom'
import { Sentry } from 'src/util/appSentry'
import { EventDialog } from '../../dialogs'
import { CALENDAR_BUFFER_DAYS, getViewRange } from './calendarUtils'

interface CalendarContextValue {
  date: Date
  setDate: (date: Date) => void
  tab: 'calendar' | 'map' | 'schedule'
  loading: boolean
  fetchingIntegrationEvents: boolean
  view: View
  setView: (view: View) => void
  hideWeekends: boolean
  setHideWeekends: (hide: boolean) => void
  events: CalendarEvent[]
  allEvents: CalendarEvent[]
  updateEvent: (event: CalendarEvent, eventTemplate?: string) => void
  addEvent: (event: CalendarEvent, eventTemplate?: string) => Promise<void>
  deleteEvent: (event: CalendarEvent, eventTemplate?: string) => void
  onMarkerClick: (event: CalendarEvent) => void
  selectedUsers?: User[]
  setSelectedUsers?: (users: User[]) => void
}

const CalendarContext = createContext<CalendarContextValue | undefined>(undefined)

export const CalendarProvider: React.FC<{
  children: React.ReactNode
  tab?: 'calendar' | 'map' | 'schedule'
}> = ({ children, tab = 'map' }) => {
  const [rawView, setRawView] = useLocalStorage<View>('calendarView', 'month')
  const view = rawView === 'agenda' ? 'day' : rawView
  const setView = (newView: View) => setRawView(newView === 'agenda' ? 'day' : newView)

  const [selectedUsers, setSelectedUsers] = useState<User[]>([])
  const { clientId } = useClientOptions()
  const { isSuperadmin, isAdmin, user } = useUser()

  const location = useLocation()

  const [date, setDateState] = useState<Date>(new Date())
  const [users, setUsers] = useState<User[]>(null)
  const [events, setEvents] = useState<CalendarEvent[]>([])
  const [googleEvents, setGoogleEvents] = useState<CalendarEvent[]>([])
  const lastFetchedMonth = useRef<number>(null)
  const lastFetchedYear = useRef<number>(null)

  const [fetchedStartDate, setFetchedStartDate] = useState<Date>(() => {
    const d = new Date()
    d.setDate(d.getDate() - CALENDAR_BUFFER_DAYS)
    return d
  })
  const [fetchedEndDate, setFetchedEndDate] = useState<Date>(() => {
    const d = new Date()
    d.setDate(d.getDate() + CALENDAR_BUFFER_DAYS)
    return d
  })

  const setDate = setDateState
  const [hideWeekends, setHideWeekends] = useState(localStorage.getItem('calendarHideWeekends') !== 'true')
  const [loading, setLoading] = useState(true)
  const { processEvent } = useProcessEvent()
  const { enqueueSnackbar } = useSnackbar()
  const [fetchingIntegrationEvents, setFetchingIntegrationEvents] = useState(false)

  const { openDialog, dismissDialog } = useDialogs()

  async function getGoogleCalendarEvents() {
    setFetchingIntegrationEvents(true)
    const { result, error } = await processEvent({
      type: 'get-all-events' as EventType,
      provider: 'google-calendar',
      params: {
        userIds: !isAdmin ? [user.user_id] : users.map((user) => user.user_id),
        date
      }
    })

    setFetchingIntegrationEvents(false)
    if (error) {
      console.error('Error fetching Google Calendar events:', error)
      return
    }
    if (users && result?.result) {
      setGoogleEvents(mapActivitiesToCalendarEvents(result.result as EventActivity[], users))
    }
  }

  useEffect(() => {
    if (!users || users.length === 0) {
      return
    }
    const currentMonth = date.getMonth()
    const currentYear = date.getFullYear()
    if (lastFetchedMonth.current !== currentMonth || lastFetchedYear.current !== currentYear) {
      getGoogleCalendarEvents()
      lastFetchedMonth.current = currentMonth
      lastFetchedYear.current = currentYear
    }
  }, [date, users])

  const handleSetWeekends = (value: boolean) => {
    localStorage.setItem('calendarHideWeekends', value ? 'false' : 'true')
    setHideWeekends(value)
  }

  const filters: Filter[] = [
    {
      id: 'details.activityType',
      type: 'equal',
      value: 'event'
    } as Filter,
    {
      id: 'trashed',
      type: 'equal',
      value: true,
      not: true
    } as Filter,
    {
      id: 'details.production',
      type: 'equal',
      value: true,
      not: true
    },
    ...(location.pathname.includes('/events')
      ? [
          {
            id: 'details.canceled',
            type: 'equal',
            value: true,
            not: true
          } as Filter
        ]
      : []),
    {
      id: 'details.end',
      type: 'greaterThan',
      value: fetchedStartDate.getTime()
    } as Filter,
    {
      id: 'details.start',
      type: 'lessThan',
      value: fetchedEndDate.getTime()
    } as Filter
  ]

  const { data: eventsData } = useSearchActivitiesQuery({
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: false,
    pollInterval: usePollingInterval(120000),
    variables: {
      query: jsonToLucene({ filters }),
      limit: null
    },
    onError: useCallback((error) => {
      console.error(error)
      enqueueSnackbar('Something went wrong, please try again later.', {
        variant: 'error',
        autoHideDuration: 3000
      })
      Sentry.captureException(error)
    }, [])
  })

  const usersQuery = isSuperadmin ? `app_metadata.companyId:"${clientId}" AND app_metadata.active:true` : ''

  const { data: usersData, loading: usersLoading } = useSearchUsersQuery({
    variables: {
      query: usersQuery,
      includeInactive: true
    },
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      const users = data?.searchUsers?.rows ?? []
      setUsers(users)
      if (users?.length === 1) {
        setSelectedUsers(users)
      } else {
        const matchingUser = users.find((_user) => _user.user_id === user?.user_id)
        setSelectedUsers(matchingUser ? [matchingUser] : [])
      }
    }
  })

  const [saveEvent] = useSaveEventMutation()

  const updateEvent = async (event: CalendarEvent, eventTemplate?: string) => {
    let updateContact = false
    if (isAdmin || event.estimator?.id === user?.user_id) {
      setEvents((prevEvents) =>
        prevEvents.map((prevEvent) => {
          if (prevEvent.id === event.id) {
            if (prevEvent?.contact?.preferredContactMethod !== event?.contact?.preferredContactMethod) {
              // need to update this contact with the new preferred contact method
              updateContact = true
            }
            return { ...prevEvent, ...event }
          }
          return prevEvent
        })
      )
    } else {
      // not admin or event owner, just remove the event from the list
      setEvents((prevEvents) => prevEvents.filter((prevEvent) => prevEvent.id !== event.id))
    }
    const result = await saveEvent({
      variables: {
        event,
        eventTemplate
      },
      refetchQueries: [updateContact ? 'getContact' : null].filter(Boolean)
    })

    // Update with the server response to pick up server-computed values (e.g. geocode after address change)
    const savedActivity = result?.data?.saveEvent?.activity
    if (savedActivity) {
      const [updatedEvent] = mapActivitiesToCalendarEvents([savedActivity], users, true)
      if (updatedEvent) {
        setEvents((prevEvents) =>
          prevEvents.map((prevEvent) =>
            prevEvent.id === updatedEvent.id ? { ...prevEvent, ...updatedEvent } : prevEvent
          )
        )
      }
    }
  }

  const addEvent = async (event: CalendarEvent, eventTemplate?: string) => {
    if (isAdmin || event.estimator?.id === user?.user_id) {
      setEvents([...events, event])
    }

    await saveEvent({
      variables: {
        event,
        eventTemplate
      },
      refetchQueries: ['searchActivities']
    })
  }

  const deleteEvent = async (event: CalendarEvent, eventTemplate?: string) => {
    setEvents((prevEvents) => prevEvents.filter((prevEvent) => prevEvent.id !== event.id))

    await saveEvent({
      variables: {
        event: { ...event, canceled: true },
        eventTemplate
      }
    })
  }

  const onMarkerClick = (event: CalendarEvent) => {
    openDialog(EventDialog, {
      event,
      onDelete: deleteEvent,
      onConfirm: (event: CalendarEvent, eventTemplate: string) => {
        updateEvent(event, eventTemplate)
        dismissDialog()
      }
    })
  }

  // Expand fetch window when date or view changes
  useEffect(() => {
    const { start, end } = getViewRange(date, view)
    if (start.getTime() < fetchedStartDate.getTime() || end.getTime() > fetchedEndDate.getTime()) {
      setFetchedStartDate(new Date(start.getTime() - CALENDAR_BUFFER_DAYS * 86400000))
      setFetchedEndDate(new Date(end.getTime() + CALENDAR_BUFFER_DAYS * 86400000))
    }
    // fetchedStartDate/fetchedEndDate are intentionally included so the check always uses
    // the latest window values; expansion is idempotent so this doesn't cause infinite loops.
  }, [date, view, fetchedStartDate, fetchedEndDate])

  useEffect(() => {
    if (users) {
      // Default to the logged-in user once users are loaded in day view
      const matchingUser = users.find((_user) => _user.user_id === user?.user_id)
      setSelectedUsers(view === 'month' ? users : [matchingUser ?? users[0]])
    }
  }, [users])

  useEffect(() => {
    if (!users?.length) {
      return
    }

    if (view !== 'month') {
      if (selectedUsers?.length > 1) {
        const matchingUser = selectedUsers.find((_user) => _user.user_id === user?.user_id)
        setSelectedUsers([matchingUser ?? selectedUsers[0]])
      }
      return
    }

    if (isAdmin) {
      const activeUsers = users.filter((user) => user.app_metadata?.active)
      const hasSameUsers =
        activeUsers.length === (selectedUsers?.length ?? 0) &&
        activeUsers.every((activeUser) =>
          selectedUsers?.some((selectedUser) => selectedUser.user_id === activeUser.user_id)
        )

      if (!hasSameUsers) {
        setSelectedUsers(activeUsers)
      }
    }
  }, [isAdmin, user, users, view])

  // The !events.length guard was intentionally removed: it was blocking events from being
  // updated after the initial load, which is required for the sliding window re-fetches to
  // take effect. updateEvent/addEvent use refetchQueries so server data stays consistent.
  useEffect(() => {
    if (eventsData && usersData) {
      setEvents(
        mapActivitiesToCalendarEvents(
          eventsData?.searchActivities?.activities as EventActivity[],
          usersData?.searchUsers?.rows as User[]
        )
      )
      setLoading(false)
    }
  }, [eventsData, usersData])

  // Listen for BroadcastChannel messages to update the activities if a new event is created from the navbar
  useEffect(() => {
    if (typeof BroadcastChannel == 'undefined') {
      return
    }
    const channel = new BroadcastChannel('header-new-button')
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.event === 'new-calendar-event') {
        const newEvent = event.data.payload as EventActivity
        const newCalendarEvent = mapActivitiesToCalendarEvents([newEvent], users)[0]
        setEvents((prevEvents) => {
          return [...prevEvents, newCalendarEvent]
        })
      }
    }
    channel.addEventListener('message', handleMessage)

    return () => {
      channel.removeEventListener('message', handleMessage)
      channel.close()
    }
  }, [users])

  const filteredEvents = filterEvents({
    date,
    events: [...events, ...googleEvents],
    selectedUsers,
    users,
    view: view as 'month' | 'week' | 'day' | 'agenda'
  })

  return (
    <CalendarContext.Provider
      value={{
        date,
        setDate,
        tab,
        loading: loading || usersLoading,
        fetchingIntegrationEvents,
        view,
        setView,
        hideWeekends,
        setHideWeekends: handleSetWeekends,
        allEvents: events,
        events: filteredEvents,
        selectedUsers,
        setSelectedUsers,
        updateEvent,
        addEvent,
        deleteEvent,
        onMarkerClick
      }}
    >
      {children}
    </CalendarContext.Provider>
  )
}

export const useCalendar = (): CalendarContextValue => {
  const context = useContext(CalendarContext)
  if (!context) {
    throw new Error('useCalendar must be used within a CalendarProvider')
  }
  return context
}
